import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AgriLinkDealsNearby = () => {
  const navigate = useNavigate();
  
  const [allProducts, setAllProducts] = useState([]);
  const [nearbyProducts, setNearbyProducts] = useState({});
  const [locationFetched, setLocationFetched] = useState(false);

  useEffect(() => {
    const fallbackProducts = [
      {
        _id: '1',
        title: 'Fresh Organic Tomatoes',
        brief: 'Locally grown, pesticide-free, fresh tomatoes.',
        category: 'Vegetables',
        latitude: 19.0759,
        longitude: 72.8772,
        image: 'tomatoes',
      },
      {
        _id: '2',
        title: 'Premium Alphonso Mangoes',
        brief: 'Sweet and juicy Alphonso mangoes, freshly harvested.',
        category: 'Fruits',
        latitude: 18.9218,
        longitude: 72.8347,
        image: 'mangoes',
      },
      {
        _id: '3',
        title: 'Organic Turmeric Powder',
        brief: 'High-quality turmeric powder from local farms.',
        category: 'Spices',
        latitude: 19.0988,
        longitude: 72.8260,
        image: 'turmeric',
      },
      {
        _id: '4',
        title: 'Free-range Eggs',
        brief: 'Fresh free-range chicken eggs, packed daily.',
        category: 'Poultry',
        latitude: 19.1197,
        longitude: 72.8468,
        image: 'eggs',
      },
      {
        _id: '5',
        title: 'Fresh Milk',
        brief: 'Farm-fresh cow milk delivered daily.',
        category: 'Dairy',
        latitude: 19.0550,
        longitude: 72.8296,
        image: 'milk',
      },
      {
        _id: '6',
        title: 'Farmed Tilapia Fish',
        brief: 'Freshwater fish, packed on the day of harvest.',
        category: 'Fishery',
        latitude: 19.1237,
        longitude: 72.9168,
        image: 'fish',
      },
    ];

    const fetchProducts = async () => {
      try {
        const res = await axios.get("/api/agri-products");
        setAllProducts(res.data?.length ? res.data : fallbackProducts);
      } catch {
        setAllProducts(fallbackProducts);
      }
    };

    fetchProducts();
  }, []);

  const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleNearbyProducts = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      const userLat = position.coords.latitude;
      const userLon = position.coords.longitude;

      const ranges = {
        'Within 2 Km': [],
        'Within 5 Km': [],
        'Within 10 Km': [],
        'Within 25 Km': [],
        'More than 25 Km': []
      };

      allProducts.forEach(product => {
        if (product.latitude && product.longitude) {
          const distance = getDistanceInKm(userLat, userLon, product.latitude, product.longitude);
          const productWithDistance = { ...product, distance };

          if (distance <= 2) ranges['Within 2 Km'].push(productWithDistance);
          else if (distance <= 5) ranges['Within 5 Km'].push(productWithDistance);
          else if (distance <= 10) ranges['Within 10 Km'].push(productWithDistance);
          else if (distance <= 25) ranges['Within 25 Km'].push(productWithDistance);
          else ranges['More than 25 Km'].push(productWithDistance);
        }
      });

      setNearbyProducts(ranges);
      setLocationFetched(true);
    }, () => {
      alert("Location access denied or failed");
    });
  };

  return (
    <div className="p-6 bg-black text-white border border-green-500 min-h-screen">
      <h2 className="text-lg lg:text-xl font-bold text-yellow-200 mb-4">Find Your Desired Agri Products Near You (if) Available Now</h2>

      <div className="mb-6">
        <button
          onClick={handleNearbyProducts}
          className="px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition"
        >
          Refresh Nearby
        </button>
      </div>

      {locationFetched && Object.keys(nearbyProducts).map(range => (
        nearbyProducts[range].length > 0 && (
          <div key={range} className="mb-8">
            <h3 className="text-xl font-semibold mb-3 text-yellow-200">{range}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {nearbyProducts[range].map(product => (
                <div key={product._id} className="bg-black border border-green-400  shadow-md p-4 rounded-xl flex flex-col">
                  <img 
                    src={`/api/downloads/agri-products/${product.image}.jpg`}
                    alt={product.title}
                    className="w-full h-40 object-cover rounded"
                  />
                  <h4 className="mt-2 text-lg font-bold text-gray-200">{product.title}</h4>
                  <p className="text-sm text-gray-300">{product.brief}</p>
                  <p className="text-sm mt-1 text-white">~{product.distance.toFixed(1)} km away</p>
                  <button 
                    onClick={() => navigate(`/agri-product/${product._id}`)}
                    className="mt-3 px-4 py-2 bg-green-500 text-white rounded-full text-sm hover:bg-green-600 transition"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
};

export default AgriLinkDealsNearby;
